import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Button,
  Typography,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as AgentIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { testChatApi, TestChatMessage } from '../../utils/testChatApi';

interface AgentChatTestDialogProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  agentName?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

const AgentChatTestDialog: React.FC<AgentChatTestDialogProps> = ({ open, onClose, agentId, agentName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<any>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setMessages([]);
      setInputValue('');
      setError(null);
      loadAgentInfo();
    }
  }, [open, agentId]);

  const loadAgentInfo = async () => {
    setLoading(true);
    try {
      const result = await testChatApi.getAgentTestInfo(agentId);
      setAgentInfo(result);
      if (!result.canTest) {
        setError('Este agente não possui cérebro (descrição) ou prompts configurados para teste.');
      }
    } catch (err) {
      setError('Erro ao carregar informações do agente');
      console.error('Erro ao carregar informações do agente:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || !agentInfo?.canTest) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setSending(true);
    setError(null);

    try {
      // Converter mensagens para formato da API
      const chatHistory: TestChatMessage[] = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp).toISOString()
      }));

      const result = await testChatApi.sendMessage({
        agentId,
        message: trimmed,
        chatHistory
      });

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.response,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      setError('Erro ao enviar mensagem');
      console.error('Erro ao enviar mensagem:', err);
      // Remove a mensagem do usuário em caso de erro
      setMessages((prev) => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setSending(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await testChatApi.clearChatHistory(agentId);
      setMessages([]);
      setError(null);
    } catch (err) {
      console.error('Erro ao limpar histórico:', err);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 0 }
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Avatar sx={{ 
            bgcolor: 'primary.main', 
            width: { xs: 24, sm: 28 }, 
            height: { xs: 24, sm: 28 } 
          }}>
            <AgentIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Testar Chat
            </Typography>
            {(agentInfo?.agentName || agentName) && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {agentInfo?.agentName || agentName}
              </Typography>
            )}
            {agentInfo && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 0.5, 
                  mt: 0.5,
                  flexWrap: 'wrap'
                }}
              >
                <Chip
                  size="small"
                  label={`Modelo: ${agentInfo.model}`}
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                />
                <Chip
                  size="small"
                  label={`Prompts: ${agentInfo.promptsCount}`}
                  variant="outlined"
                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                />
                {agentInfo.hasBrain && (
                  <Chip
                    size="small"
                    label="Cérebro ativo"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{ 
            position: { xs: 'absolute', sm: 'static' },
            top: { xs: 8, sm: 'auto' },
            right: { xs: 8, sm: 'auto' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        dividers 
        sx={{ 
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 120px)', sm: '500px' }
        }}
      >
        {/* Aviso sobre limitações */}
        {agentInfo && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ 
              m: { xs: 1, sm: 2 }, 
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              flexShrink: 0
            }}
          >
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {agentInfo.warning}
            </Typography>
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            flex: 1,
            minHeight: 200 
          }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && !loading && (
          <Alert severity="error" sx={{ m: 2, flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        {/* Chat messages - Scrollable area */}
        {!loading && !error && (
          <Box 
            ref={scrollRef} 
            sx={{ 
              p: { xs: 1, sm: 2 }, 
              flex: 1,
              overflowY: 'auto', 
              bgcolor: 'background.default',
              minHeight: 0 // Important for flex scrolling
            }}
          >
            {messages.length === 0 && (
              <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 }, color: 'text.secondary' }}>
                <AgentIcon sx={{ fontSize: { xs: 32, sm: 48 }, mb: 2, opacity: 0.5 }} />
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  Comece uma conversa para testar o cérebro e prompts do agente
                </Typography>
              </Box>
            )}

            {messages.map((m) => (
              <Box 
                key={m.id} 
                sx={{ 
                  display: 'flex', 
                  mb: { xs: 1, sm: 1.5 }, 
                  alignItems: 'flex-start', 
                  gap: { xs: 1, sm: 1.5 }, 
                  flexDirection: m.role === 'user' ? 'row-reverse' : 'row' 
                }}
              >
                <Avatar sx={{ 
                  bgcolor: m.role === 'user' ? 'grey.600' : 'primary.main', 
                  width: { xs: 24, sm: 30 }, 
                  height: { xs: 24, sm: 30 } 
                }}>
                  {m.role === 'user' ? <PersonIcon fontSize="small" /> : <AgentIcon fontSize="small" />}
                </Avatar>
                <Box sx={{ 
                  maxWidth: { xs: '85%', sm: '75%' }, 
                  bgcolor: m.role === 'user' ? 'grey.800' : 'grey.100', 
                  color: m.role === 'user' ? 'grey.100' : 'grey.900', 
                  px: { xs: 1, sm: 1.5 }, 
                  py: { xs: 0.75, sm: 1 }, 
                  borderRadius: 2 
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      lineHeight: { xs: 1.3, sm: 1.4 }
                    }}
                  >
                    {m.content}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Loading indicator for sending message */}
            {sending && (
              <Box sx={{ 
                display: 'flex', 
                mb: { xs: 1, sm: 1.5 }, 
                alignItems: 'flex-start', 
                gap: { xs: 1, sm: 1.5 } 
              }}>
                <Avatar sx={{ 
                  bgcolor: 'primary.main', 
                  width: { xs: 24, sm: 30 }, 
                  height: { xs: 24, sm: 30 } 
                }}>
                  <AgentIcon fontSize="small" />
                </Avatar>
                <Box sx={{ 
                  bgcolor: 'grey.100', 
                  px: { xs: 1, sm: 1.5 }, 
                  py: { xs: 0.75, sm: 1 }, 
                  borderRadius: 2 
                }}>
                  <CircularProgress size={16} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 1, 
                      display: 'inline',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    Processando...
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Fixed Input Area - Always visible */}
        <Box sx={{ 
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-end', 
            gap: { xs: 0.5, sm: 1 }, 
            p: { xs: 1, sm: 1.5 } 
          }}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              placeholder={agentInfo?.canTest ? "Digite sua mensagem..." : "Agente não disponível para teste"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!agentInfo?.canTest || sending || loading}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!agentInfo?.canTest || sending || loading || inputValue.trim().length === 0}
              sx={{
                padding: { xs: '8px', sm: '12px' },
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        justifyContent: 'space-between',
        p: { xs: 1, sm: 2 }
      }}>
        {/* <Button
          onClick={handleClearHistory}
          startIcon={<ClearIcon />}
          disabled={messages.length === 0 || loading}
          color="warning"
        >
          Limpar Histórico
        </Button> */}
        <Button 
          onClick={onClose}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentChatTestDialog;


